<template>
  <div class="otp-step">
    <div class="text-center mb-2">
      <h2 class="mb-1">Validation du devis</h2>
      <p class="text-muted">
        Veuillez entrer le code de validation à 6 chiffres envoyé par email
      </p>
    </div>

    <b-form @submit.prevent="handleSubmit">
      <b-form-group
        label="Code de validation"
        label-for="otp-digit-0"
        label-class="text-center d-block"
      >
        <div ref="inputsWrapper" class="otp-inputs" @paste="handlePaste">
          <b-form-input
            v-for="(digit, index) in digits"
            :id="`otp-digit-${index}`"
            :key="index"
            :value="digit"
            :formatter="digitsOnly"
            :state="errorMessage ? false : null"
            :disabled="isLoading"
            :aria-label="`Chiffre ${index + 1}`"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="1"
            class="otp-input"
            @input="handleInput(index, $event)"
            @keydown="handleKeydown(index, $event)"
            @focus="$event.target.select()"
          />
        </div>
      </b-form-group>

      <b-spinner v-if="isLoading" small class="mt-2" />
    </b-form>
  </div>
</template>

<script>
import { BForm, BFormGroup, BFormInput, BSpinner } from "bootstrap-vue";
import { computed, nextTick, onMounted, ref, watch } from "@vue/composition-api";

const CODE_LENGTH = 6;

export default {
  name: "QuoteValidationStepOtp",
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BSpinner,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: null,
    },
  },
  setup(props, { emit }) {
    const digits = ref(Array(CODE_LENGTH).fill(""));
    const inputsWrapper = ref(null);

    const otpCode = computed(() => digits.value.join(""));
    const isComplete = computed(() => otpCode.value.length === CODE_LENGTH);

    // `b-form-input` applies the formatter before emitting `input`: non-numeric
    // characters are ignored and the field is resynchronized
    const digitsOnly = (value) => value.replace(/\D/g, "");

    const inputElements = () =>
      inputsWrapper.value
        ? Array.from(inputsWrapper.value.querySelectorAll("input"))
        : [];

    const focusIndex = (index) => {
      const input = inputElements()[index];
      if (input) {
        input.focus();
        input.select();
      }
    };

    const setDigit = (index, value) => {
      const next = [...digits.value];
      next[index] = value;
      digits.value = next;
    };

    // Écrit les chiffres à partir d'une position donnée, renvoie la position suivante
    const fillFrom = (index, value) => {
      const chars = digitsOnly(value).split("");
      if (!chars.length) return index;

      const next = [...digits.value];
      let cursor = index;
      chars.forEach((char) => {
        if (cursor < CODE_LENGTH) {
          next[cursor] = char;
          cursor += 1;
        }
      });
      digits.value = next;

      return Math.min(cursor, CODE_LENGTH - 1);
    };

    const handleSubmit = () => {
      if (isComplete.value && !props.isLoading) {
        emit("verify", otpCode.value);
      }
    };

    const handleInput = (index, value) => {
      // `value` is already filtered by the formatter, multiple digits can
      // only come from autofill (code received by SMS)
      if (value.length > 1) {
        const cursor = fillFrom(0, value);
        nextTick(() => {
          syncInputElements();
          focusIndex(cursor);
          handleSubmit();
        });
        return;
      }

      setDigit(index, value);
      if (!value) return;

      focusIndex(Math.min(index + 1, CODE_LENGTH - 1));
      if (isComplete.value) handleSubmit();
    };

    const handleKeydown = (index, event) => {
      switch (event.key) {
        case "Backspace":
          event.preventDefault();
          if (digits.value[index]) {
            setDigit(index, "");
            nextTick(() => focusIndex(index));
          } else if (index > 0) {
            setDigit(index - 1, "");
            nextTick(() => focusIndex(index - 1));
          }
          break;
        case "Delete":
          event.preventDefault();
          setDigit(index, "");
          break;
        case "ArrowLeft":
          event.preventDefault();
          focusIndex(Math.max(index - 1, 0));
          break;
        case "ArrowRight":
          event.preventDefault();
          focusIndex(Math.min(index + 1, CODE_LENGTH - 1));
          break;
        default:
          break;
      }
    };

    // `maxlength="1"` would truncate the clipboard: we distribute the pasted code
    const handlePaste = (event) => {
      const pasted = (event.clipboardData || window.clipboardData).getData(
        "text"
      );
      if (!pasted) return;

      event.preventDefault();
      const cursor = fillFrom(0, pasted);
      nextTick(() => {
        focusIndex(cursor);
        handleSubmit();
      });
    };

    // Le remplissage automatique peut désynchroniser la valeur interne du champ
    const syncInputElements = () => {
      inputElements().forEach((input, index) => {
        if (input.value !== digits.value[index]) {
          input.value = digits.value[index];
        }
      });
    };

    // Réinitialise la saisie lorsqu'une erreur de vérification remonte
    watch(
      () => props.errorMessage,
      (errorMessage, previous) => {
        if (errorMessage && !previous) {
          digits.value = Array(CODE_LENGTH).fill("");
          nextTick(() => focusIndex(0));
        }
      }
    );

    onMounted(() => focusIndex(0));

    return {
      digits,
      inputsWrapper,
      digitsOnly,
      handleInput,
      handleKeydown,
      handlePaste,
      handleSubmit,
    };
  },
};
</script>

<style scoped>
.otp-step {
  max-width: 400px;
  margin: 0 auto;
}

.otp-inputs {
  display: flex;
  justify-content: center;
}

.otp-input {
  flex: 0 0 auto;
  width: 3rem;
  height: 3.5rem;
  /* Neutralise l'icône de validation de Bootstrap, illisible dans une case */
  padding: 0;
  background-image: none;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
}

.otp-input + .otp-input {
  margin-left: 0.5rem;
}

@media (max-width: 400px) {
  .otp-input {
    width: 2.5rem;
    height: 3rem;
    font-size: 1.25rem;
  }

  .otp-input + .otp-input {
    margin-left: 0.25rem;
  }
}
</style>
